import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import PageShell from '../components/PageShell';
import Tabs from '../components/Tabs';
import ViewSwitch from '../components/ViewSwitch';
import { useHubExcelExport } from '../components/ExcelExportButton';
import PrintReportDocument from './PrintReportDocument';
import { downloadFile } from '../api/client';
import { usePersistentValue } from '../hooks/usePersistentState';
import { ReportingContext } from '../components/ReportingControls';
import './FinancialsReportsHub.css';
import { resolveTabs } from '../i18n/resolveTabs';
import { planningLevel } from '../nav/planningLevel';
import ProfitLossPage from './ProfitLossPage';
import FinancialsSynthesisSimple from './FinancialsSynthesisSimple';
import BalanceSheetPage from './BalanceSheetPage';
import CashFlowPage from './CashFlowPage';
import ForecastCashPlanTab from './ForecastCashPlanTab';
import FinancialsWorkingCapitalTab from './FinancialsWorkingCapitalTab';

/* LES ÉTATS FINANCIERS, en LECTURE SEULE — un seul écran pour les sept.
     • Synthèse            (FinancialsSynthesisSimple)
     • Compte de résultat  (P&L)
     • Bilan               (Balance Sheet)
     • Flux de trésorerie  (Cash Flow)
     • Plan de liquidité   (méthode directe)
     • BFRE                (besoin en fonds de roulement d'exploitation)
     • Ratio liquidité

   **ACCÈS DIRECT (2026-09-17)** : chaque état est une SECTION du menu Finance,
   plus un onglet d'un hub « Rapports ». On ouvre le Bilan d'un clic, là où il
   fallait ouvrir Rapports puis chercher son onglet. Le drapeau `only` dit
   quel(s) état(s) la section montre (`only` accepte une LISTE, comme dans
   `HrPlanningHub` : « Indicateurs financiers » réunit le BFRE et le ratio de
   liquidité sous un sélecteur, sans seconde rangée de boutons).

   C'est le MÊME composant qui sert les six sections : le contexte de lecture
   (vue, arrêté, comparaison, unité) est donc partagé au niveau du MODULE, et
   non recréé par écran. Sa mémoire vit dans les clés `fin_reports_*` du
   stockage de session : passer du compte de résultat au bilan garde la vue
   Prévisionnel et l'arrêté choisis. Sans ce partage, l'accès direct dégraderait
   la lecture au lieu de l'améliorer.

   Sans `only`, la page reste le hub à six onglets, ROUTÉ HORS MENU : plus rien
   n'y renvoie depuis que les drawers du Cockpit visent les sections, elle reste
   le filet de sécurité de la transition.

   Séparation par métier (2026-08-09) : la saisie du réel (import, cellules,
   affectation des comptes, ajustements) est partie dans le hub **Données** ;
   la structure reste dans **Paramètres**. Un rapport ne s'édite plus.

   **Vue = dimension du rapport, pas section de menu** : le sélecteur Réel /
   Prévisionnel change les colonnes d'un même état, sans changer d'écran. Il ne
   s'affiche que sur les états qui savent déjà servir les deux (compte de
   résultat, bilan) — le cash flow et le BFR ont encore deux moteurs distincts,
   ils suivront avec l'unification des moteurs. La vue Prévisionnel lit la MÊME
   source que le hub Prévisionnel : les deux ne peuvent pas diverger.
   PLUS DE BUDGET FIGÉ (décision Olivier, 2026-09-27) : la vue Prévisionnel lit
   la VERSION DE PLAN choisie dans la bande de contexte, et le compte de
   résultat se compare à N-1 (par défaut) ou à une version de plan, qui se
   recalcule à la lecture à partir de SES saisies.
   Les VUES DE DIRECTION (Rentabilité, Liquidité & solidité, Financement,
   Focus+) restent au **Cockpit** : ici on lit les états, là on croise les
   diagnostics.
   Année pilotée par le sélecteur global de la bande de page — pas de picker
   local pour éviter le doublon. Gabarit unique : <PageShell framed>. */
// États sachant servir les deux vues (mêmes données, colonnes différentes).
const VIEW_AWARE = new Set(['pl', 'bs', 'cf', 'bfre', 'liquidity']);

/* Les états, dans l'ordre de lecture. `cashplan` n'est PAS un onglet du hub
   relais : il a sa section, et il vivait jusqu'ici dans la Trésorerie. */
const STATE_DEFS = [
  { id: 'synthesis', labelKey: 'finance:reports.tabs.synthesis' },
  { id: 'pl', labelKey: 'finance:reports.tabs.pl' },
  { id: 'bs', labelKey: 'finance:reports.tabs.bs' },
  { id: 'cf', labelKey: 'finance:reports.tabs.cf' },
  { id: 'cashplan', labelKey: 'finance:reports.tabs.cashplan' },
  { id: 'bfre', labelKey: 'finance:reports.tabs.bfre' },
  { id: 'liquidity', labelKey: 'finance:reports.tabs.liquidity' },
];
const HUB_TAB_IDS = ['synthesis', 'pl', 'bs', 'cf', 'bfre', 'liquidity'];
const TAB_DEFS = STATE_DEFS.filter((s) => HUB_TAB_IDS.includes(s.id));

/* Raccourcis d'accès direct, exportés pour App.js — une section, ses états. */
export const REPORT_STATES = {
  synthesis: ['synthesis'],
  pl: ['pl'],
  bs: ['bs'],
  cf: ['cf'],
  cashPlan: ['cashplan'],
  indicators: ['bfre', 'liquidity'],
};

export default function FinancialsReportsHub({ activeTenant, addToast, year, onYearChange,
                                              only = null, title = null, setPage = null }) {
  const { t } = useTranslation();
  const p = useP();
  /* `only` = la ou les vues de la section. Sans lui, le hub à onglets. */
  const states = useMemo(() => {
    if (!only) return null;
    const ids = Array.isArray(only) ? only : [only];
    return ids.filter((id) => STATE_DEFS.some((s) => s.id === id));
  }, [only]);
  const direct = !!states;
  const TABS = resolveTabs(
    direct ? STATE_DEFS.filter((s) => states.includes(s.id)) : TAB_DEFS,
    t,
  );
  const [picked, setPicked] = useState(direct ? states[0] : 'synthesis');
  const tab = direct
    ? (states.includes(picked) ? picked : states[0])
    : picked;
  const setTab = setPicked;
  const [showPrint, setShowPrint] = useState(false);
  /* (Le relais `reports_pending_tab` est retiré le 2026-09-17 : les drawers du
     Cockpit visent maintenant la SECTION de l'état, plus un onglet de ce hub.
     Il n'y a donc plus d'onglet à demander au montage.) */
  // Unité d'affichage CHF / kCHF — pilotée au niveau du MODULE (même clé pour
  // les six sections) et appliquée à tous les états.
  const [unit, setUnit] = usePersistentValue(`fin_reports_unit_${activeTenant?.id}`, 'kchf');
  // Vue lue : 'actual' (mois clôturés) ou 'forecast' (12 mois, réel + prévision).
  const [view, setView] = usePersistentValue(`fin_reports_view_${activeTenant?.id}`, 'actual');
  /* LES VERSIONS DE PLAN, celles du sélecteur de la bande de contexte : on
     compare le réel à l'une d'elles (2026-09-27). */
  const rep = useContext(ReportingContext);
  const versions = useMemo(() => rep?.versions || [], [rep?.versions]);
  // L'ancienne vue « Budget » retenue par un navigateur retombe sur le réel.
  const effView = view === 'budget' ? 'actual' : view;
  // Arrêté d'affichage (vue Réel, 2026-08-20) : borner les rapports à un mois
  // ANTÉRIEUR à la clôture — « arrêter les comptes à fin juin » alors que
  // juillet est déjà clos. Le serveur borne les vues (`through`) : cumulés,
  // comparatifs et réconciliation suivent. La clôture du tenant reste la
  // seule vérité comptable ; défaut = clôture.
  const closedMonth = (() => {
    const c = rep?.closed;
    if (!c) return 0;
    const cy = Number(c.slice(0, 4)), cm = Number(c.slice(5, 7));
    return year < cy ? 12 : year > cy ? 0 : cm;
  })();
  /* L'arrêté SUIT la navigation (2026-09-17) : il appartient au contexte de
     lecture du module, comme la vue et l'unité. Sa clé porte l'exercice, si
     bien que changer d'année ou de client repart de la clôture (null). */
  const [cut, setCut] = usePersistentValue(`fin_reports_cut_${activeTenant?.id}_${year}`, null);
  const cutEff = (effView === 'actual' && cut && cut < closedMonth) ? cut : null;
  const forecastView = effView === 'forecast' && VIEW_AWARE.has(tab);

  // Comparaison : exercice N-1 (défaut) ou une version de plan. Le compte de résultat
  // porte déjà les colonnes ; seule la SOURCE change.
  const [compare, setCompare] = usePersistentValue(`fin_reports_cmp_${activeTenant?.id}`, 'n1');
  /* PÉRIODE de la synthèse (mois · cumulé · exercice) : elle pilote ses DEUX
     cartes, le compte de résultat et le cash flow, donc elle est de portée
     PAGE — elle a vécu une heure dans l'entête de la première carte, où elle
     avait l'air de ne piloter que celle-là. */
  const [period, setPeriod] = usePersistentValue(`fin_reports_period_${activeTenant?.id}`, 'ytd');
  /* LE PLAN DE LIQUIDITÉ portait cinq pilules dans l'entête de sa carte
     (Graphique · Plan · TVA · Charges sociales · dont TVA), qui mélangeaient
     deux natures : « Plan » et « Graphique » sont deux VUES de la même donnée,
     « TVA » et « Charges sociales » d'AUTRES tables. Les aligner laissait
     croire qu'on pouvait voir « le graphique de la TVA », ce qui n'existe pas.
     UNE SEULE commande dans la rangée (2026-09-18) : la REPRÉSENTATION.
     Les FLUX (TVA, charges sociales) sont redescendus dans l'entête de la
     carte, où « dont TVA » vivait déjà : ils ne piloteraient pas une seconde
     carte si la page en portait une, donc l'étage 4. L'état, lui, reste ici :
     il appartient au contexte de lecture du module, qui survit au passage d'un
     état à l'autre. Choisir « Graphique » ramène au plan, le graphique étant
     celui du solde. */
  const [planRepr, setPlanRepr] = usePersistentValue(`fin_plan_repr_${activeTenant?.id}`, 'table');
  const planPane = planRepr === 'chart' ? 'chart' : 'plan';
  /* Le composant garde son propre `setPane` pour les gestes qui changent de vue
     depuis l'écran ; il ne reste que deux représentations. */
  const setPlanPane = (v) => setPlanRepr(v === 'chart' ? 'chart' : 'table');
  const cmpVersion = tab === 'pl' ? versions.find((v) => v.id === compare) || null : null;


  // Rendu d'un état — réutilisé à l'écran (unité du module) et pour l'export
  // Excel (unité forcée à CHF). `u` = unité effective.
  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    switch (id) {
      case 'pl':          return <ProfitLossPage embedded cutoff={cutEff} actualsOnly={!forecastView} withPrevYear={forecastView} readOnly
                            compareVersion={cmpVersion ? cmpVersion.id : null} compareLabel={cmpVersion ? cmpVersion.label : null} unit={u} activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />;
      /* La SYNTHÈSE porte le sélecteur de période depuis que celle du
         Prévisionnel a fusionné ici (2026-09-17) : mois, cumulé, ou exercice
         entier (réel + prévision). L'export le laisse au cumulé, un classeur
         n'ayant pas de commande à cliquer. */
      case 'synthesis':   return <FinancialsSynthesisSimple framed liquidityHeader period={forExport ? null : period} cutoff={cutEff} activeTenant={activeTenant} year={year} unit={u} />;
      case 'bs':          return <BalanceSheetPage embedded cutoff={cutEff} actualsOnly monthsFull={forecastView} flatForecast={forecastView} readOnly unit={u} activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />;
      case 'cf':          return <CashFlowPage embedded cutoff={cutEff} actualsOnly forecastView={forecastView} unit={u} activeTenant={activeTenant} addToast={addToast} year={year} />;
      /* Le PLAN DE LIQUIDITÉ se LIT ici (2026-09-17) : il quitte la section
         Trésorerie, qui garde ses supports de construction (créances, dettes,
         acomptes, TVA, charges sociales, financement). Même composant, donc
         un seul moteur et une seule vérité. */
      case 'cashplan':    return <ForecastCashPlanTab activeTenant={activeTenant} year={year} unit={u}
                            pane={forExport ? 'plan' : planPane}
                            onPane={forExport ? undefined : setPlanPane} />;
      case 'bfre':        return <FinancialsWorkingCapitalTab section="treasury" cutoff={cutEff} forecastView={forecastView} activeTenant={activeTenant} year={year} unit={u} />;
      case 'liquidity':   return <FinancialsWorkingCapitalTab section="liquidity" cutoff={cutEff} forecastView={forecastView} activeTenant={activeTenant} year={year} unit={u} />;
      default: return null;
    }
  };

  /* LES COMMANDES DE LECTURE passent au GABARIT (2026-09-17) : elles sont de
     portée PAGE, donc `PageShell views=` les place sous le titre, dans un ordre
     et un costume uniques. Une page ne les pose plus à la main.
     L'ORDRE suit la stabilité, pas l'importance : les PERMANENTES viennent en
     premier (la base de lecture, la période, l'indicateur), les commandes qui
     peuvent DISPARAÎTRE ferment la rangée (l'arrêté, qui n'a pas de sens sur
     l'exercice ; la comparaison, qui demande une version de plan). La rangée
     s'alignant à GAUCHE depuis le 2026-09-18, c'est dans ce sens que ce qui
     s'efface ne déplace plus rien : la bascule de période garde sa place quand
     l'arrêté s'en va. *(La règle était inverse la veille, quand la rangée
     fermait la ligne du titre à droite — l'ordre suit l'alignement.)*
     La place du SUJET reste tenue pour le jour où un état se déclinera en
     plusieurs rapports : ici, c'est le MENU qui joue ce rôle, chaque état
     ayant sa section. */
  /* Une section à DEUX états (les indicateurs) les porte sur une bascule de la
     rangée des vues (2026-09-18) : elle choisit CE QU'ON LIT, donc elle est de
     portée page, et une rangée d'onglets pour deux vues ferait une commande de
     plus sur un second rang. Elle a vécu une journée dans l'entête. */
  const stateSwitch = direct && states.length > 1 ? {
    id: 'state', kind: 'segmented', label: p('Indicateur'),
    value: tab, onChange: setTab,
    options: TABS.map((tb) => ({ id: tb.id, label: tb.label })),
  } : null;
  const monthLbl = closedMonth > 0 ? t(`common:months.${closedMonth}`) : '';
  const planViews = (direct && states.includes('cashplan')) ? [
    {
      id: 'repr', kind: 'segmented', label: p('Vue'),
      value: planRepr,
      onChange: setPlanRepr,
      options: [
        { id: 'table', label: p('Table') },
        { id: 'chart', label: p('Graphique') },
      ],
    },
  ] : [];

  const views = [
    VIEW_AWARE.has(tab) ? {
      id: 'base', kind: 'segmented', label: t('finance:reports.view.label'),
      value: view, onChange: setView,
      options: [
        { id: 'actual', label: t('finance:reports.view.actual') },
        { id: 'forecast', label: t('finance:reports.view.forecast') },
      ],
    } : null,
    (direct && states.includes('synthesis')) ? {
      id: 'period', kind: 'segmented', label: p('Période'),
      value: period, onChange: setPeriod,
      options: [
        { id: 'month', label: `${p('Mois')} · ${monthLbl}` },
        { id: 'ytd', label: `${p('Cumulé')} · ${closedMonth} ${p('mois')}` },
        { id: 'year', label: `${p('Exercice')} ${year}` },
      ],
    } : null,
    stateSwitch,
    ...planViews,
    (effView === 'actual' && closedMonth > 0 && tab !== 'cashplan' && period !== 'year') ? {
      id: 'cut', kind: 'select', label: t('finance:reports.cutLabel'),
      value: String(cut || closedMonth),
      onChange: (v) => { const n = Number(v); setCut(n === closedMonth ? null : n); },
      options: Array.from({ length: closedMonth }, (_, i) => i + 1)
        .map((m) => ({ id: String(m), label: `${t(`common:months.${m}`)} ${year}` })),
    } : null,
    /* COMPARER À : N-1 par défaut, ou une version de plan (2026-09-27). Une
       liste, les versions pouvant être nombreuses. */
    (versions.length && tab === 'pl') ? {
      id: 'compare', kind: 'select', label: t('finance:reports.view.compareLabel'),
      value: cmpVersion ? cmpVersion.id : 'n1', onChange: setCompare,
      options: [
        { id: 'n1', label: t('finance:reports.view.compareN1') },
        ...versions.map((v) => ({ id: v.id, label: v.label })),
      ],
    } : null,
  ].filter(Boolean);

  /* L'unité monte au CADRE (2026-09-17) : elle tenait une seconde ligne de
     commandes sur la ligne des onglets, à côté de celle qui porte déjà
     l'exercice et l'export. Le gabarit la transmet, la bande de page la rend. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);

  // Exports de la page → l'icône télécharger de la bande de page (registre
  // pageExports, décision 2026-08-18) : un seul bouton, deux formats.
  // Excel = les états de la section, en CHF. PDF = le dossier de reporting
  // (« Réalisé » + ratios), réservé à la SYNTHÈSE et au hub relais : le sortir
  // depuis le seul bilan sortirait autre chose que ce qu'on lit.
  const sectionName = direct ? (title || TABS[0]?.label || 'Rapports') : 'Rapports';
  const withPdf = !direct || states.includes('synthesis');
  const excel = useHubExcelExport({
    tabs: TABS.map((tb) => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: sectionName,
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  /* RAPPORT PDF (2026-10-16) : le rapport financier complet (synthèse, compte
     de résultat, bilan, flux, ratios), fabriqué au serveur depuis les mêmes
     calculs que les écrans. Présent dans TOUTES les sections du hub, jusqu'à
     l'arrêté d'affichage choisi. */
  const downloadReport = useCallback(async () => {
    if (!activeTenant?.id) return;
    const through = cutEff ? `&through=${cutEff}` : '';
    try {
      await downloadFile(`/financials/report/${activeTenant.id}/pdf?year=${year}${through}`,
        `${activeTenant.name || 'Rapport'} · ${year} · Rapport financier.pdf`);
    } catch (e) {
      addToast?.(e.message, 'error');
    }
  }, [activeTenant?.id, activeTenant?.name, year, cutEff, addToast]);
  const exportOptions = useMemo(() => [
    { id: 'excel', label: 'Excel', run: excel.run },
    { id: 'report', label: p('Rapport PDF'), run: downloadReport },
    ...(withPdf ? [{ id: 'pdf', label: 'PDF', run: () => setShowPrint(true) }] : []),
  // eslint-disable-next-line
  ], [activeTenant?.id, year, withPdf, downloadReport]);

  /* (Le lien « Voir le Cockpit » a quitté l'entête le 2026-09-18 : le menu
     mène au Cockpit en un clic, et un renvoi de plus sur la ligne du titre
     poussait le contexte sur un second rang.) */

  return (
    <PageShell framed kind="report" unit={pageUnit} exports={exportOptions}
               title={direct ? sectionName : t('finance:reports.title')}
               views={views}>
      {/* Hub relais : le nom de section vit SUR la ligne d'onglets (2026-08-20). */}
      {!direct && <Tabs tabs={TABS} active={tab} onChange={setTab} />}

      {/* LE MANUEL EST UN EMBRANCHEMENT (décision Olivier, 2026-09-30) : les
          états de Finance se calculent toujours en Pro, sur la comptabilité ;
          un client réglé sur Manuel planifie sa trésorerie à part, et Finance
          le dit, sans quoi deux trésoreries se liraient sans explication. */}
      {planningLevel(activeTenant) === 'manual' && (
        <div className="frh-branch">
          {p('Le plan de liquidité est en Manuel : il n\'est plus relié à votre comptabilité et s\'écarte de ces états prévisionnels, calculés en Pro.')}
        </div>
      )}

      <div className="page-shell__panel">
        {renderTab(tab)}
      </div>

      {showPrint && (
        <PrintReportDocument
          activeTenant={activeTenant}
          year={year}
          reports={['financials-standard', 'liquidity-ratios']}
          reportProps={{ cutoff: cutEff }}
          mode="pdf"
          onClose={() => setShowPrint(false)}
        />
      )}
      {excel.stage}
    </PageShell>
  );
}
